import React from "react";
import type { EdgeHubStatusDto } from "../models/edgeMonitorAdminModels";

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";

const COLORS = {
  green: "#2D8C4E",
  greenBg: "#E8EFE6",
  amber: "#D4B040",
  amberBg: "#F8F0DD",
  terracotta: "#D4572B",
  terracottaBg: "#FCEBE3",
  greyDot: "#8C9099",
  greyBg: "#F0EFE9",
  primaryText: "#1A1A1A",
  dimText: "#6B6B6B",
};

export interface Zone1EdgeHubTileProps {
  /** Phase A admin DTO. `undefined` = never fetched yet, `null` = 403 / 404 / cross-tenant. */
  data: EdgeHubStatusDto | null | undefined;
  /** True while the first fetch is in flight (no prior data to render). */
  loadingNoData: boolean;
  /** True when the server returned 404 NO_SITE_AGENT (machine has no MachineConnection row). */
  noSiteAgent?: boolean;
  /** True when the server returned 403 / cross-tenant. */
  notAvailable: boolean;
  /**
   * Heartbeat side-channel from the existing SiteAgent state. When the agent is
   * heartbeating REST-style (recent LastHeartbeat) but `isConnected=false`, we
   * render the amber "heartbeating but no hub" state per spec §3.1.
   */
  heartbeatRecent: boolean;
}

interface PillVisual {
  bg: string;
  textColor: string;
  dot: string;
  text: string;
  textBold: boolean;
}

const computePill = (
  data: EdgeHubStatusDto | null | undefined,
  loadingNoData: boolean,
  noSiteAgent: boolean | undefined,
  notAvailable: boolean,
  heartbeatRecent: boolean
): PillVisual => {
  if (loadingNoData) {
    return {
      bg: COLORS.greyBg,
      textColor: COLORS.dimText,
      dot: COLORS.greyDot,
      text: "Loading…",
      textBold: false,
    };
  }
  if (notAvailable && !data) {
    return {
      bg: COLORS.greyBg,
      textColor: COLORS.dimText,
      dot: COLORS.greyDot,
      text: "No access",
      textBold: false,
    };
  }
  if (noSiteAgent || (data && data.siteAgentId === null)) {
    return {
      bg: COLORS.terracottaBg,
      textColor: COLORS.terracotta,
      dot: COLORS.terracotta,
      text: "No site agent",
      textBold: true,
    };
  }
  if (data && data.isConnected) {
    return {
      bg: COLORS.greenBg,
      textColor: COLORS.green,
      dot: COLORS.green,
      text: "Connected",
      textBold: true,
    };
  }
  // Not connected. Differentiate "heartbeating but no hub" (amber) from
  // "fully offline" (terracotta).
  if (heartbeatRecent) {
    return {
      bg: COLORS.amberBg,
      textColor: "#9C7A0F",
      dot: COLORS.amber,
      text: "Heartbeat · no hub",
      textBold: true,
    };
  }
  return {
    bg: COLORS.terracottaBg,
    textColor: COLORS.terracotta,
    dot: COLORS.terracotta,
    text: "Not connected",
    textBold: true,
  };
};

const Zone1EdgeHubTile: React.FC<Zone1EdgeHubTileProps> = ({
  data,
  loadingNoData,
  noSiteAgent,
  notAvailable,
  heartbeatRecent,
}) => {
  const pill = computePill(data, loadingNoData, noSiteAgent, notAvailable, heartbeatRecent);
  // The DTO already truncates the connection id to 8 chars server-side
  // (SEC-REQ-3). We render it verbatim — never re-truncate or expand client-side
  // — so any backend regression that leaks more chars stays visible to QA.
  const connId = data?.connectionId ?? null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: "10px",
          letterSpacing: "0.1em",
          color: COLORS.dimText,
          textTransform: "uppercase",
        }}
      >
        Edge Hub
      </div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "5px 11px",
          borderRadius: "12px",
          background: pill.bg,
          alignSelf: "flex-start",
        }}
      >
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: pill.dot,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontFamily: FONT_SORA,
            fontSize: "12px",
            fontWeight: pill.textBold ? 600 : 500,
            color: pill.textColor,
            letterSpacing: "0.02em",
          }}
        >
          {pill.text}
        </span>
      </div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: "11px",
          color: COLORS.dimText,
          letterSpacing: "0.04em",
          minHeight: "14px",
        }}
      >
        {connId ? `conn ${connId}` : " "}
      </div>
    </div>
  );
};

export default Zone1EdgeHubTile;
